/**
 * The machine-first walk-through's model (spec `observer-autogen-review-walkthrough`, R1–R4): the
 * engine's per-machine view for one camera, with THIS session's decisions laid over it. Pure — the
 * host owns the read and the session.
 *
 * The engine judged the plan as it was SAVED when it was read (`#2149`), so a suggestion accepted a
 * moment ago still reads `not-watched` in the answer. The overlay is what keeps the list honest
 * between a decision and the next read.
 */
import type {
  HeatGridPart,
  ObserverProposal,
  ObserverProposalCamera,
  ObserverProposalMachine,
  ObserverProposalMachineOutline,
  ObserverProposalMachineReason,
  ObserverProposalMachineState,
} from '@repo/frontend-types';

export type DiscardReason = 'wrong-place' | 'wrong-machine' | 'not-a-machine';

/**
 * A review decision. *Keep mine* (R4.3, R5.3) holds while the machine's suggestion stays on this
 * part. *Discard* (`observer-autogen-review-discard-and-scores`, R1) holds while the suggestion stays
 * on this part AND within half its size of where it was (R1.7). No `kind` means Keep mine.
 */
export interface SuggestionDecision {
  targetRefid: string;
  cameraBid: string;
  trackSide: HeatGridPart;
  decidedAt: string;
  kind?: 'keep' | 'discard';
  reason?: DiscardReason;
  centroid?: { x: number; y: number };
  size?: number;
}

/** A suggestion's centre and the longer side of its bounding box, picture px. */
export const regionShape = (
  points: readonly { x: number; y: number }[],
): { centroid: { x: number; y: number }; size: number } => {
  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const y0 = Math.min(...ys);
  const y1 = Math.max(...ys);
  return {
    centroid: { x: (x0 + x1) / 2, y: (y0 + y1) / 2 },
    size: Math.max(x1 - x0, y1 - y0),
  };
};

/**
 * Whether a Discard still applies to the machine's current suggestion (R1.7): same part, and its
 * centre no further than half the discarded region's size from where it was. A materially
 * different suggestion asks again; the record stays in the plan, unused.
 */
export const discardApplies = (
  d: SuggestionDecision,
  proposal: ObserverProposal | null,
): boolean => {
  if (d.kind !== 'discard' || !proposal || !d.centroid || !d.size) return false;
  if (d.trackSide !== proposal.observer.trackSide || proposal.observer.points.length === 0) {
    return false;
  }
  const { centroid } = regionShape(proposal.observer.points);
  return Math.hypot(centroid.x - d.centroid.x, centroid.y - d.centroid.y) <= d.size / 2;
};

/** What the session changed since the answer was read, for the camera shown. */
export interface ReviewSession {
  /** Machines given a region on this camera by Use, Adjust or Wrong machine this session. */
  acceptedTargets: ReadonlySet<string>;
  /** Drawn observer refids deleted this session. */
  deletedDrawnRefids: ReadonlySet<string>;
  /** Every Keep mine decision on the plan. */
  decisions: readonly SuggestionDecision[];
  /**
   * Suggestions whose region is already in use on ANOTHER machine on this camera — re-linked there
   * with Wrong machine. Derived from the session, so an Undo gives them back (Copilot, #2156).
   */
  takenProposalRefids?: ReadonlySet<string>;
}

export const EMPTY_SESSION: ReviewSession = {
  acceptedTargets: new Set(),
  deletedDrawnRefids: new Set(),
  decisions: [],
};

/** How the list reads a machine. The engine's five states, plus why a settled one is settled. */
export type ReviewState = ObserverProposalMachineState;
export type SettledBy = 'engine' | 'accepted' | 'kept' | 'discarded' | null;

export interface ReviewMachine {
  targetRefid: string;
  name: string;
  state: ReviewState;
  reason: ObserverProposalMachineReason;
  settledBy: SettledBy;
  /** The engine could not clearly beat a drawn observer (Mike's ruling 3): settled, with a note. */
  suggestionDiffers: boolean;
  partlyInView: boolean;
  /**
   * Where the engine thinks the machine is on the picture: its floor-plan outline through each view
   * it was projected through, in view order (spec `observer-matching-beyond-geometry-machine-outline`).
   */
  outlines: ObserverProposalMachineOutline[];
  /** The machine's one suggestion, or null (no heat, or nothing it could name). */
  proposal: ObserverProposal | null;
  drawnObserverRefids: string[];
  /** Eligible for *Accept all confident* (R4.6). */
  acceptAllEligible: boolean;
  /** The suggestion now watches another machine (Wrong machine): it can't be taken here too. */
  proposalTaken: boolean;
  /** The suggestion was discarded and still matches what was discarded (R1.5, R1.7). */
  discarded: boolean;
  discardReason: DiscardReason | null;
  /** The engine's heat score for the compared drawn observer, on its own part; null if none. */
  drawnScore: number | null;
  /** A drawn observer the engine compared was deleted this session: its score is no longer known. */
  drawnChanged: boolean;
}

export type FallbackReason = 'not-calibrated' | 'camera-skipped' | 'camera-failed';

export interface MachineReview {
  machines: ReviewMachine[];
  progress: { settled: number; inView: number };
  unexplained: ObserverProposal[];
  unexplainedTotal: number;
  /** Set when the engine could not place machines: the list shows the inventory instead (R2.5). */
  fallback: FallbackReason | null;
}

/** The work left first (R2.2). */
const ORDER: Record<ReviewState, number> = {
  'not-watched': 0,
  better: 1,
  'name-uncertain': 2,
  'no-heat': 3,
  settled: 4,
};

const isConfident = (p: ObserverProposal): boolean => p.confident;

const keptFor = (
  decisions: readonly SuggestionDecision[],
  cameraBid: string,
  m: ObserverProposalMachine,
  proposal: ObserverProposal | null,
): boolean =>
  !!proposal &&
  decisions.some(
    d =>
      (d.kind ?? 'keep') === 'keep' &&
      d.cameraBid === cameraBid &&
      d.targetRefid === m.targetRefid &&
      d.trackSide === proposal.observer.trackSide,
  );

const discardFor = (
  decisions: readonly SuggestionDecision[],
  cameraBid: string,
  m: ObserverProposalMachine,
  proposal: ObserverProposal | null,
): SuggestionDecision | null =>
  decisions.find(
    d =>
      d.cameraBid === cameraBid && d.targetRefid === m.targetRefid && discardApplies(d, proposal),
  ) ?? null;

const fallbackReview = (fallback: FallbackReason): MachineReview => ({
  machines: [],
  progress: { settled: 0, inView: 0 },
  unexplained: [],
  unexplainedTotal: 0,
  fallback,
});

/**
 * The list for one camera.
 *
 * ⚠ A camera the engine could not place machines on is NEVER `{ machines: [] }` — that would read as
 * "nothing to review" on a camera nobody has reviewed (R2.5). It carries a fallback reason instead.
 */
export const reviewMachines = (
  camera: ObserverProposalCamera,
  session: ReviewSession,
): MachineReview => {
  if (camera.status === 'failed') return fallbackReview('camera-failed');
  if (camera.status !== 'ok' || !camera.result) return fallbackReview('camera-skipped');
  const result = camera.result;
  if (result.machines === null) return fallbackReview('not-calibrated');
  const proposalByRefid = new Map(result.proposals.map(p => [p.observer.refid, p]));
  const machines = result.machines.map((m): ReviewMachine => {
    const proposal = m.proposalRefid ? (proposalByRefid.get(m.proposalRefid) ?? null) : null;
    const drawn = m.drawnObserverRefids.filter(r => !session.deletedDrawnRefids.has(r));
    let state: ReviewState = m.state;
    let reason = m.reason;
    let settledBy: SettledBy = m.state === 'settled' ? 'engine' : null;
    if (session.acceptedTargets.has(m.targetRefid)) {
      state = 'settled';
      settledBy = 'accepted';
    } else if (
      drawn.length > 0 &&
      keptFor(session.decisions, camera.rtspEndpointBid, m, proposal)
    ) {
      state = 'settled';
      settledBy = 'kept';
    } else if (m.drawnObserverRefids.length > 0 && drawn.length === 0) {
      // Every drawn observer the engine compared went this session: nothing watches it now — but a
      // tied name is still tied, so its candidates stay on show (Copilot, #2156).
      state =
        m.state === 'name-uncertain' || proposal?.nameUncertain
          ? 'name-uncertain'
          : proposal
            ? 'not-watched'
            : 'no-heat';
      // ...and the engine's reason described the observer that has gone (Copilot, #2156).
      reason =
        state === 'name-uncertain'
          ? 'tied-with'
          : state === 'not-watched'
            ? 'suggested'
            : 'no-heat-region';
      settledBy = null;
    }
    const proposalTaken = !!proposal && !!session.takenProposalRefids?.has(proposal.observer.refid);
    // Discard (R1.5). A machine watched now - drawn before the read, or given an observer this
    // session - keeps its observer and settles behind the discard (Copilot, #2190).
    const discard = discardFor(session.decisions, camera.rtspEndpointBid, m, proposal);
    if (discard && (drawn.length > 0 || settledBy === 'accepted')) {
      // Your observer stays and nothing else is left to decide.
      state = 'settled';
      settledBy = 'discarded';
    }
    const suggestionDiffers = settledBy === 'engine' && m.reason === 'drawn-holds';
    return {
      targetRefid: m.targetRefid,
      name: m.targetName ?? proposal?.targetName ?? m.targetRefid,
      state,
      reason,
      settledBy,
      suggestionDiffers,
      partlyInView: m.partlyInView,
      outlines: m.outlines,
      proposal,
      drawnObserverRefids: drawn,
      acceptAllEligible:
        state === 'not-watched' &&
        !!proposal &&
        !proposalTaken &&
        !discard &&
        isConfident(proposal),
      proposalTaken,
      discarded: !!discard,
      discardReason: discard?.reason ?? null,
      // The engine scored ONE of the drawn observers and does not say which, so once any of them
      // is deleted this session the score may describe one that has gone (Copilot, #2190).
      drawnScore: drawn.length === m.drawnObserverRefids.length ? (m.drawnScore ?? null) : null,
      drawnChanged: drawn.length !== m.drawnObserverRefids.length,
    };
  });
  machines.sort((a, b) => ORDER[a.state] - ORDER[b.state] || a.name.localeCompare(b.name));
  return {
    machines,
    progress: {
      settled: machines.filter(m => m.state === 'settled').length,
      inView: machines.length,
    },
    unexplained: result.unexplainedHeat,
    unexplainedTotal: result.unexplainedHeatTotal,
    fallback: null,
  };
};

/**
 * *Accept all confident* (R4.6): not-watched machines with a confident suggestion and no drawn
 * observer. Never a better, name-uncertain or settled machine — a drawn observer is never replaced.
 */
export const acceptAllTargets = (review: MachineReview): ReviewMachine[] =>
  review.machines.filter(
    m =>
      m.state === 'not-watched' &&
      m.proposal !== null &&
      !m.proposalTaken &&
      !m.discarded &&
      isConfident(m.proposal),
  );

/** The next (or previous) machine still needing a decision, wrapping (R3.1). */
export const nextUnsettled = (
  review: MachineReview,
  currentRefid: string | null,
  direction: 1 | -1,
): ReviewMachine | null => {
  // A discarded machine has had its decision (R1.6), even when nothing watches it yet.
  const open = review.machines.filter(m => m.state !== 'settled' && !m.discarded);
  if (open.length === 0) return null;
  const at = currentRefid ? open.findIndex(m => m.targetRefid === currentRefid) : -1;
  if (at === -1) return direction === 1 ? open[0] : open[open.length - 1];
  return open[(at + direction + open.length) % open.length];
};

/** One plain sentence for why a machine is in its state (R4.1). */
export const reasonText = (m: ReviewMachine): string => {
  if (m.settledBy === 'accepted') return 'Suggestion used this session.';
  if (m.settledBy === 'kept') return 'You kept your observer.';
  if (m.settledBy === 'discarded') return 'You discarded the suggestion; your observer stays.';
  if (m.discarded) return 'You discarded the suggestion. Nothing watches this machine yet.';
  if (m.reason === 'drawn-holds') {
    const suggested = m.proposal?.evidence?.partScore;
    if (m.drawnScore !== null && typeof suggested === 'number') {
      if (m.drawnScore >= suggested) return 'Yours scores higher — kept.';
      const points = Math.round((suggested - m.drawnScore) * 100);
      return `The suggestion scores ${points} point${points === 1 ? '' : 's'} higher — not enough to replace yours, so yours is kept.`;
    }
  }
  switch (m.reason) {
    case 'no-heat-region':
      return 'No activity found where the calibration puts this machine — check its place on the plan.';
    case 'tied-with':
      return 'The heat could belong to more than one machine — check which.';
    case 'agrees':
      return 'Your observer agrees with the suggestion.';
    case 'drawn-holds':
      return 'Suggestion differs, but not clearly better than your observer.';
    case 'better-part':
      return 'The engine finds a body part that catches this machine’s users better.';
    case 'better-place':
      return 'The engine finds a region that fits this machine’s heat better.';
    case 'suggested':
    default:
      return 'Nothing watches this machine yet.';
  }
};

export const STATE_TEXT: Record<ReviewState, string> = {
  'not-watched': 'Not watched',
  better: 'Better suggestion',
  'name-uncertain': 'Name uncertain',
  'no-heat': 'No heat — check the plan',
  settled: 'Settled',
};

const pct = (v: number): string => `${Math.round(v * 100)}%`;

/**
 * The comparison card's Heat score row (spec `observer-autogen-review-discard-and-scores`, R2): your
 * drawn observer's heat score beside the suggestion's - the two numbers the engine compared
 * (`machine-review.ts`: `drawnScore` against `evidence.partScore`), not the suggestion's confidence.
 * `drawnPart` names your observer's part when every drawn observer shares one. Null when there is
 * nothing to compare: no drawn observer, no suggestion, or an engine that sends no evidence.
 */
export const heatScoreLine = (
  m: ReviewMachine,
  drawnPart: string | null,
  partLabel: (part: HeatGridPart) => string,
): string | null => {
  const suggested = m.proposal?.evidence?.partScore;
  if (m.drawnObserverRefids.length === 0 || !m.proposal || typeof suggested !== 'number') {
    return null;
  }
  const theirs = `Suggested ${partLabel(m.proposal.observer.trackSide)} · ${pct(suggested)}`;
  if (m.drawnScore === null) {
    return m.drawnChanged
      ? `Yours changed since the suggestions were read · ${theirs}`
      : `Yours can't be scored · ${theirs}`;
  }
  const diff = Math.round((suggested - m.drawnScore) * 100);
  const yours = drawnPart
    ? `Yours ${drawnPart} · ${pct(m.drawnScore)}`
    : `Yours ${pct(m.drawnScore)}`;
  return `${yours} · ${theirs} (${diff >= 0 ? '+' : '−'}${Math.abs(diff)})`;
};
